/**
 * HTML sanitization utilities for preventing XSS vulnerabilities.
 *
 * This module provides safe HTML sanitization using DOMPurify to prevent
 * XSS attacks while preserving safe formatting tags. Content is assumed to
 * come from trusted sources (backend/content files), but sanitization is
 * applied as defense-in-depth security measure.
 *
 * @module sanitize
 */

import DOMPurify from 'dompurify';

/**
 * Configuration for DOMPurify to allow safe formatting tags only
 * 
 * Allowed tags:
 * - strong, u, br, p, span, div: Safe formatting tags
 * 
 * All dangerous tags (script, iframe, object, embed, etc.) are automatically stripped.
 * All dangerous attributes (onclick, onerror, onload, etc.) are automatically stripped.
 */
const SANITIZE_CONFIG = {
  ALLOWED_TAGS: ['strong', 'u', 'br', 'p', 'span', 'div'],
  ALLOWED_ATTR: [], // No attributes allowed for maximum security
  KEEP_CONTENT: true, // Keep text content even if tags are stripped
} as const satisfies Partial<DOMPurify.Config>;

/**
 * Sanitizes HTML content to prevent XSS attacks while preserving safe formatting
 * 
 * This function uses DOMPurify to strip dangerous HTML tags and attributes
 * while preserving safe formatting tags like <strong>, <u>, and <br>.
 * 
 * **Security:**
 * - Strips all script tags and event handlers
 * - Strips dangerous attributes (onclick, onerror, etc.)
 * - Only allows safe formatting tags
 * 
 * **Formatting preserved:**
 * - Bold text: **text** → <strong>text</strong>
 * - Underline: <u>text</u> → <u>text</u>
 * - Line breaks: \n → <br>
 * 
 * @param html - HTML string to sanitize (may contain markdown-style formatting)
 * @returns Sanitized HTML string safe for rendering with dangerouslySetInnerHTML
 * 
 * @example
 * ```typescript
 * const formatted = formatMarkdown("**Bold** text");
 * const safe = sanitizeHtml(formatted);
 * <div dangerouslySetInnerHTML={{ __html: safe }} />
 * ```
 * 
 * @example
 * ```typescript
 * // Malicious content is stripped:
 * sanitizeHtml("<script>alert('XSS')</script>");
 * // Returns: "" (script tag stripped)
 * 
 * // Safe formatting is preserved:
 * sanitizeHtml("<strong>Bold</strong> text");
 * // Returns: "<strong>Bold</strong> text"
 * ```
 */
export function sanitizeHtml(html: string): string {
  if (!html) return '';
  
  // Sanitize using DOMPurify with safe configuration
  return DOMPurify.sanitize(html, SANITIZE_CONFIG);
}

